/**
 * calculateLeadScore.js
 *
 * Computes an initial lead score (1–10) at quote submission time using
 * form data signals. Two conditional pipelines split on whether the
 * cart total is strictly greater than $50,000 or $50,000 and under.
 *
 * Score meaning:
 *   8–10 → Hot   (strong signals, prioritise call)
 *   6–7  → Warm  (good signals, follow up)
 *   4–5  → Cool  (mixed or weak signals)
 *   1–3  → Cold  (low-conversion profile)
 *
 * Called from useSubmitQuoteForm.js after submitQuote() resolves:
 *   const score = calculateLeadScore(formData, items, totalPrice);
 */

// ---------------------------------------------------------------------------
// Universal signals
// ---------------------------------------------------------------------------

const BASE = 0;

// ── 1. Timeline ─────────────────────────────────────────────────────────────
const TIMEFRAME = {
  'ASAP':                                        2.5,
  'In the coming weeks':                         1,
  'In the coming months':                        0.5,
  '3 to 6 months from now':                      0,
  'Within the year':                             0,
  "I don't know, mainly gathering information": -1,
};

// ── 2. Tax exempt ───────────────────────────────────────────────────────────
const TAX_EXEMPT_BONUS = 2.5;

// ---------------------------------------------------------------------------
// Conditional pipeline: Org-type scores by cart-total bracket
// ---------------------------------------------------------------------------

const ORG_HIGH = {
  "YMCA, JCC, or Other Community Center":          1,
  'Daycare or Preschool':                          1,
  'Non-profit (Headstart, Hospital, etc.)':        1,
  'Governmental Entity (Parks Dept, Public Works, etc.)': 1,
  'Construction or Landscaping company':           1,
  'Church or Place of Worship':                    2,
  'Public School':                                 2,
  'Homeowners Association':                       -2,
  'Private School':                               -2,
  'Private Home':                                 -2,
};

const ORG_LOW = {
  "YMCA, JCC, or Other Community Center":          1,
  'Non-profit (Headstart, Hospital, etc.)':        1,
  'Church or Place of Worship':                    1,
  'Public School':                                 1,
  'Campground, Summer Camp, etc.':                 1,
  'Country/Golf Club':                             1,
  'Governmental Entity (Parks Dept, Public Works, etc.)': 1,
  'Construction or Landscaping company':           0.5,
  'Daycare or Preschool':                         -0.5,
  'For-profit Business (Restaurants, Car Wash, etc.)': -0.5,
  'Homeowners Association':                       -1,  
  'Management Company':                           -1,
  'Private Home':                                 -1,
};

// ---------------------------------------------------------------------------
// Conditional pipeline: Budget scoring by cart-total bracket
// ---------------------------------------------------------------------------

// >$50k: "Budget Reality" — scored by label alone
const BUDGET_REALITY = {
  'Over $50,000':                    2,
  '$30,000 to $50,000':              1,
  "I haven't established a budget": -1,
  '$20,000 to $30,000':             -1,
  '$15,000 to $20,000':             -1,
  '$10,000 to $15,000':             0,
  '$5,000 to $10,000':              0,
  'Under $5,000':                   -1,
};

// <=50k: "Budget Accuracy" — compare total to upper bound of stated budget
const BUDGET_UPPER_BOUNDS = {
  'Under $5,000':          5000,
  '$5,000 to $10,000':    10000,
  '$10,000 to $15,000':   15000,
  '$15,000 to $20,000':   20000,
  '$20,000 to $30,000':   30000,
  '$30,000 to $50,000':   50000,
  'Over $50,000':        Infinity,
};

function budgetAccuracyPoints(budgetSelect, totalDollars) {
  if (!budgetSelect || budgetSelect === "I haven't established a budget") return 0;
  const upper = BUDGET_UPPER_BOUNDS[budgetSelect];
  if (upper == null) return 0;
  if (totalDollars < upper) return 3;
  if (totalDollars <= upper * 1.2) return 0.5;
  if (totalDollars <= upper * 1.5) return -1;
  return -2;
}

// ---------------------------------------------------------------------------
// Kept signals (unchanged from prior version)
// ---------------------------------------------------------------------------

// ── Installation requested ──────────────────────────────────────────────────
const INSTALLATION_PENALTY = -0.5;

// ── Surfacing requested ─────────────────────────────────────────────────────
const SURFACING_PENALTY = -0.5;

// ── Buyer authority — looking_for ───────────────────────────────────────────
const LOOKING_FOR = {
  'The best equipment for my approved budget':  0.5,
  'The right equipment for my organization':    0.5,
  'Pricing to take to our decision maker(s)': -0.5,
};

// ── Special requests ────────────────────────────────────────────────────────
const SPECIAL_REQUESTS_PENALTY = -0.5;

// ── Accommodations ──────────────────────────────────────────────────────────
const ACCOMMODATION_EXISTING_PLAYGROUND = 0.5;
const ACCOMMODATION_COMPLEXITY_PENALTY  = -0.25;
const ACCOMMODATION_COMPLEXITY_FLAGS = [
  'I need to remove old equipment (pictures required for quoting)',
  'Construction equipment may have difficulty accessing my site',
  'My project will require permitting (Customer\'s responsibility)',
];

// ── Geographic penalty (West Coast / distant states) ────────────────────────
const STATE_SCORE = {
  'CA': -1,
  'AZ': -1,
  'WA': -0.5,
  'MO': 0.5,
  'VA': 0.5,
  'IL': 0.5,
  'IN': 0.5,
  'OK': 0.5,
  'MI': 0.5,
  'AL': 0.5,
  'GA': 0.5,
  'OH': 0.5,
};

// ── Number of line items ────────────────────────────────────────────────────
function lineItemPoints(itemCount) {
  if (!itemCount || itemCount < 1) return 0;
  if (itemCount === 1)              return  0.75;
  if (itemCount === 2)              return  0.25;
  if (itemCount <= 3)               return -0.2;
  if (itemCount <= 10)              return  1;
  if (itemCount <= 15)              return  1.5;
  return 0.5;
}

// ---------------------------------------------------------------------------
// Main scoring function
// ---------------------------------------------------------------------------

/**
 * @param {Object} formData     - Raw form field values (from quoteForm state)
 * @param {Array}  [items]      - Cart line items (array, for item-count signal)
 * @param {number} [totalPrice] - Cart total in CENTS
 * @returns {{ score: number, scoreRaw: number, breakdown: Object }}
 */
export function calculateLeadScore(
  formData = {},
  items = [],
  totalPrice = 0,
) {
  let score = BASE;
  const breakdown = {};
  const totalDollars = (totalPrice || 0) / 100;
  const isHighValue = totalDollars > 50000;

  // ── 1. Timeline ───────────────────────────────────────────────────────────
  const timeframePts = TIMEFRAME[formData.timeframe_select] ?? 0;
  breakdown.timeframe = timeframePts;
  score += timeframePts;

  // ── 2. Tax exempt ─────────────────────────────────────────────────────────
  const taxPts = formData.tax_exempt === 'Yes' ? TAX_EXEMPT_BONUS : 0;
  breakdown.tax_exempt = taxPts;
  score += taxPts;

  // ── 3. Organization type (conditional on cart total) ───────────────────────
  const buyingFor = formData.select_buying_for || '';
  const orgMap = isHighValue ? ORG_HIGH : ORG_LOW;
  const orgPts = orgMap[buyingFor] ?? 0;
  breakdown.buying_for = orgPts;
  score += orgPts;

  // ── 4. Budget (conditional on cart total) ─────────────────────────────────
  const budgetSelect = formData.budget_select || '';
  let budgetPts;
  if (isHighValue) {
    budgetPts = BUDGET_REALITY[budgetSelect] ?? 0;
  } else {
    budgetPts = budgetAccuracyPoints(budgetSelect, totalDollars);
  }
  breakdown.budget = budgetPts;
  score += budgetPts;

  // ── 5. Installation requested ─────────────────────────────────────────────
  // const installPts = formData.quote_installation === 'Yes' ? INSTALLATION_PENALTY : 0;
  // breakdown.installation = installPts;
  // score += installPts;

  // ── 6. Surfacing requested ────────────────────────────────────────────────
  const surfacingPts = (formData.surfacing_request === 'Yes' && formData.surfacing_type !== 'Rubber Mulch') ? SURFACING_PENALTY : 0;
  breakdown.surfacing = surfacingPts;
  score += surfacingPts;

  // ── 7. Buyer authority (looking_for) ──────────────────────────────────────
  const lookingForPts = LOOKING_FOR[formData.looking_for] ?? 0;
  breakdown.looking_for = lookingForPts;
  score += lookingForPts;

  // ── 8. Special requests ───────────────────────────────────────────────────
  const specialRequests = Array.isArray(formData.special_requests)
    ? formData.special_requests
    : (formData.special_requests ? [formData.special_requests] : []);
  const specialPts = specialRequests.length > 0 ? SPECIAL_REQUESTS_PENALTY : 0;
  breakdown.special_requests = specialPts;
  score += specialPts;

  // ── 9. Accommodations ────────────────────────────────────────────────────
  const accommodations = Array.isArray(formData.accommodations)
    ? formData.accommodations
    : (formData.accommodations ? [formData.accommodations] : []);
  let accommodationPts = 0;
  if (accommodations.includes('I am adding to an existing playground')) {
    accommodationPts += ACCOMMODATION_EXISTING_PLAYGROUND;
  }
  const complexityCount = ACCOMMODATION_COMPLEXITY_FLAGS
    .filter((flag) => accommodations.includes(flag)).length;
  accommodationPts += Math.max(-0.6, complexityCount * ACCOMMODATION_COMPLEXITY_PENALTY);
  breakdown.accommodations = Math.round(accommodationPts * 10) / 10;
  score += accommodationPts;

  // ── 10. State / geography ──────────────────────────────────────────────────
  const statePts = STATE_SCORE[formData.state] ?? 0;
  breakdown.state = statePts;
  score += statePts;

  // ── 11. Number of line items ──────────────────────────────────────────────
  const itemCount = Array.isArray(items) ? items.length : 0;
  const itemsPts = lineItemPoints(itemCount);
  breakdown.line_items = itemsPts;
  score += itemsPts;

  // ── Clamp and round to 1–10 ──────────────────────────────────────────────
  const scoreRaw   = Math.round(score * 10) / 10;
  const scoreFinal = Math.min(10, Math.max(1, Math.round(score)));

  breakdown.pipeline = isHighValue ? '>50k' : '<=50k';

  return {
    score:     scoreFinal,
    scoreRaw,
    breakdown,
  };
}
